import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, ExternalLink, Clock, GitCommit, AlertTriangle } from 'lucide-react';
import { useCIStatus } from '@/hooks/useCIStatus';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function CurrentCIStatus() {
  const { data: ciStatus, isLoading, error } = useCIStatus();
  
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 animate-spin" />
            Carregando Status do CI...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }
  
  if (error || !ciStatus) {
    return (
      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-muted-foreground">
            <AlertTriangle className="h-5 w-5" />
            Status do CI Indisponível
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Nenhuma execução de CI encontrada ainda.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const isSuccess = ciStatus.status === 'success';
  const isRunning = ciStatus.status === 'running';
  const timeAgo = formatDistanceToNow(new Date(ciStatus.completed_at), { 
    addSuffix: true, 
    locale: ptBR 
  });
  
  return (
    <Card className={`
      ${isSuccess ? 'border-green-500/50 bg-green-500/5' : ''}
      ${!isSuccess && !isRunning ? 'border-red-500/50 bg-red-500/5' : ''}
      ${isRunning ? 'border-yellow-500/50 bg-yellow-500/5' : ''}
    `}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {isSuccess && <CheckCircle2 className="h-5 w-5 text-green-500" />}
            {!isSuccess && !isRunning && <XCircle className="h-5 w-5 text-red-500" />}
            {isRunning && <Clock className="h-5 w-5 text-yellow-500 animate-spin" />}
            
            Status do CI - Branch {ciStatus.branch_name}
          </CardTitle>
          
          <Badge variant={isSuccess ? 'default' : 'destructive'}>
            {ciStatus.status.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Commit Info */}
        <div className="flex items-start gap-3">
          <GitCommit className="h-4 w-4 text-muted-foreground mt-1" />
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                {ciStatus.commit_sha.substring(0, 7)}
              </code>
              <span className="text-sm text-muted-foreground">
                por {ciStatus.commit_message ? `${ciStatus.commit_message.split('\n')[0].substring(0, 50)}` : 'Unknown'}
              </span>
            </div>
            <p className="text-sm">
              {ciStatus.commit_message || 'Sem mensagem de commit'}
            </p>
            <p className="text-xs text-muted-foreground">{timeAgo}</p>
          </div>
        </div>
        
        {/* Failure Details */}
        {!isSuccess && !isRunning && ciStatus.failure_stage && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
            <p className="text-sm font-medium text-destructive mb-1">
              ❌ Falha na etapa: {ciStatus.failure_stage}
            </p>
            {ciStatus.failure_details?.failed_tests && (
              <p className="text-sm text-muted-foreground">
                {ciStatus.failure_details.failed_tests} de {ciStatus.failure_details.total_tests} testes falharam
              </p>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2">
          {ciStatus.workflow_url && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open(ciStatus.workflow_url, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ver no GitHub Actions
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
